import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, Activity } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", clinic_name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success("Welcome to CONTAHA");
      navigate("/consultation/new");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F9F8]">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-[#07131A] contaha-grid p-12 overflow-hidden">
        <div className="relative z-10">
          <Logo size={34} light />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#223139] bg-[#0E1A21] px-3 py-1 text-xs font-semibold text-[#3FD98A] mb-6">
            <Sparkles className="h-3.5 w-3.5" /> AI-assisted clinical workflow
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-white leading-tight">
            The clinical operating system for modern implant practices.
          </h1>
          <p className="mt-4 text-[#8B9698] text-base leading-relaxed">
            Patient management, dynamic clinical forms, implant planning, and AI-drafted reports — with the clinician always in control.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: Activity, text: "End-to-end clinical case journeys" },
              { icon: Sparkles, text: "AI drafts, you review and approve" },
              { icon: ShieldCheck, text: "Premium, branded clinical reports" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-white/90">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00A85A]/15 text-[#3FD98A]">
                  <f.icon className="h-4 w-4" />
                </div>
                {f.text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-[#4E5F68]">© 2026 CONTAHA · AI assists. Clinician decides.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><div className="inline-flex rounded-xl bg-[#07131A] px-3 py-2"><Logo light /></div></div>
          <h2 className="text-2xl font-bold text-[#101516]">{mode === "login" ? "Sign in to CONTAHA" : "Create your account"}</h2>
          <p className="mt-1 text-sm text-[#687173]">{mode === "login" ? "Welcome back, doctor." : "Set up your clinical workspace."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-semibold text-[#101516]">Full name</Label>
                <Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. Jane Doe" className="mt-1.5 h-11" required />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-[#101516]">Email</Label>
              <Input data-testid="login-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@clinic.com" className="mt-1.5 h-11" required />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#101516]">Password</Label>
              <Input data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-1.5 h-11" required />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg font-semibold">
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-[#8B9698]">
            <div className="h-px flex-1 bg-[#E2E8F0]" /> OR <div className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <Button onClick={googleLogin} variant="outline" data-testid="google-login" className="w-full h-11 rounded-lg border-[#E2E8F0] font-medium">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-sm text-[#687173]">
            {mode === "login" ? "New to CONTAHA?" : "Already have an account?"}{" "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-auth-mode" className="font-semibold text-[#00A85A] hover:text-[#008F4D]">
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
